/* eslint-disable @typescript-eslint/naming-convention */
import {
  ref,
  readonly,
  computed,
  useContext,
} from '@nuxtjs/composition-api';
import type { Ref } from '@nuxtjs/composition-api';
import mask from '~/composables/utils/mask';
import { Logger } from '~/helpers/logger';
import { useCustomerStore } from '~/modules/customer/stores/customer';
import { useCart } from '~/modules/checkout/composables/useCart';
import { generateUserData } from '~/modules/customer/helpers/generateUserData';
import { useUiNotification } from '~/composables/useUiNotification';
import type { AttributesMetadataOutput, Customer } from '~/modules/GraphQL/types';
import customerNotifyInStock from '~/modules/GraphQL/customQueries/customerNotifyInStock.gql';
import customer from '~/modules/GraphQL/customQueries/customer.gql';
import customMergeCartsGql from '~/modules/GraphQL/customQueries/mergeCarts.gql';
import amSocialLoginAccountData from '~/modules/GraphQL/customQueries//amSocial/amSocialLoginAccountData.gql';
import amSocialLoginButtonConfig from '~/modules/GraphQL/customQueries//amSocial/amSocialLoginButtonConfig.gql';
import amSocialLoginLinkAccount from '~/modules/GraphQL/customQueries//amSocial/amSocialLoginLinkAccount.gql';
import amSocialLoginUnlinkAccount from '~/modules/GraphQL/customQueries//amSocial/amSocialLoginUnlinkAccount.gql';
import customAttributeMetadataV2Gql from '~/modules/GraphQL/customQueries/customAttributeMetadataV2.gql';
import createCustomerV2Gql from '~/modules/GraphQL/customQueries/createCustomerV2.gql';

import customerCart from '~/modules/GraphQL/customQueries/customerCart.gql';
import { CustomerCartQuery } from '~/modules/GraphQL/types';
import {
  UseUserInterface,
  UseUserErrors,
  UseUserLoadParams,
  UseUserLoginParams,
  UseUserLogoutParams,
  UseUserRegisterParams,
  UseUserUpdateUserParams,
  UseUserChangePasswordParams,
  CustomerNotifyInStock,
} from './useUser';

export type CustomUseUserUpdateUserParams = UseUserUpdateUserParams & {
  currentPassword: string;
  newPassword: string;
};
/**
 * Allows loading and manipulating data of the current user.
 *
 * See the {@link UseUserInterface} for a list of methods and values available in this composable.
 */
export function useUser(): UseUserInterface {
  const customerStore = useCustomerStore();
  // @ts-ignore
  const { app, $recaptcha } = useContext();
  const { setCart } = useCart();
  const { send: sendNotification } = useUiNotification();
  const loading: Ref<boolean> = ref(false);
  const errorsFactory = () : UseUserErrors => ({
    updateUser: null,
    register: null,
    login: null,
    loginWithToken: null,
    linkSocialAccount: null,
    unlinkSocialAccount: null,
    loadAccountData: null,
    loadButtonConfig: null,
    logout: null,
    changePassword: null,
    load: null,
    customAttributeMetadataV2: null,
  });
  const error: Ref = ref(errorsFactory());

  const setUser = (newUser: Customer) => {
    customerStore.user = newUser;
    Logger.debug('useUserFactory.setUser', newUser);
  };

  const resetErrorValue = () => {
    error.value = errorsFactory();
  };

  const updateCustomerEmail = async (credentials: { email: string, password: string }): Promise<void> => {
    const { errors } = await app.context.$vsf.$magento.api.updateCustomerEmail(credentials);

    if (errors) {
      throw errors.map((e) => e.message).join(',');
    }
  };

  const saveCustomerNotify = async (productId:string): Promise<CustomerNotifyInStock> => {
    Logger.debug('[Magento] save customer notify when product in stock', productId);
    let results = null;

    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({ query: customerNotifyInStock, queryVariables: { productId } });
      results = data?.CustomerNotifyInStock;
      Logger.debug('[Magento] save customer notify results:', results);
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('[Magento] save customer notify error', err);
    } finally {
      loading.value = false;
    }
    return results;
  };

  // eslint-disable-next-line consistent-return
  const changePassword = async (params: UseUserChangePasswordParams) => {
    Logger.debug('[Magento] useUser.changePassword', { currentPassword: mask(params.current), newPassword: mask(params.new) });
    resetErrorValue();

    try {
      loading.value = true;

      const { data, errors } = await app.context.$vsf.$magento.api.changeCustomerPassword({
        currentUser: customerStore.user,
        currentPassword: params.current,
        newPassword: params.new,
      }, params.customQuery, params?.customHeaders);

      let joinedErrors = null;

      if (errors) {
        joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
      }

      Logger.debug('[Result] ', { data });

      customerStore.user = data?.changeCustomerPassword;
      error.value.changePassword = joinedErrors;
    } catch (err) {
      error.value.changePassword = err;
      Logger.error('useUser/changePassword', err);
    } finally {
      loading.value = false;
    }
  };

  // eslint-disable-next-line consistent-return
  const updateUser = async ({
    user: providedUser, currentPassword, newPassword, customQuery, customHeaders,
  }: CustomUseUserUpdateUserParams) => {
    Logger.debug('[Magento] Update user information', { providedUser, customQuery, customHeaders });
    resetErrorValue();

    try {
      loading.value = true;
      const { email: oldEmail } = customerStore.user;
      const { email, ...updateData } = providedUser;

      const userData = generateUserData(updateData);

      if (email && email !== oldEmail) {
        await updateCustomerEmail({ email, password: currentPassword });
      }

      if (currentPassword.length > 0 && newPassword.length > 0) {
        await changePassword({ current: currentPassword, new: newPassword });
      }

      const { data, errors } = await app.context.$vsf.$magento.api.updateCustomer(userData, customQuery, customHeaders);
      Logger.debug('[Result]:', { data });

      if (errors) {
        const allErrorMessages = errors.map((e) => e.message).join(',');
        Logger.error(allErrorMessages);
        error.value.updateUser = allErrorMessages;
      }

      customerStore.user = data?.updateCustomerV2?.customer || {};
      error.value.updateUser = null;
    } catch (err) {
      error.value.updateUser = err;
      Logger.error('useUser/updateUser', err);
    } finally {
      loading.value = false;
    }
  };

  const logout = async ({ customQuery = {}, customHeaders = {} }: UseUserLogoutParams = {}) => {
    Logger.debug('[Magento] useUserFactory.logout');
    resetErrorValue();

    try {
      const apiState = app.context.$vsf.$magento.config.state;

      await app.context.$vsf.$magento.api.revokeCustomerToken(customQuery, customHeaders);

      apiState.removeCustomerToken();
      apiState.removeCartId();
      apiState.removeQuoteId();
      setCart(null);
      customerStore.setIsLoggedIn(false);
      error.value.logout = null;
      customerStore.user = null;
    } catch (err) {
      error.value.logout = err;
      Logger.error('useUser/logout', err);
    }
  };

  const load = async ({ customHeaders = {} }: UseUserLoadParams = {}) => {
    Logger.debug('[Magento] useUser.load');
    resetErrorValue();

    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;

      if (!apiState.getCustomerToken()) {
        return null;
      }
      try {
        const { data } = await app.context.$vsf.$magento.api.customQuery({ query: customer, customHeaders });
        Logger.debug('[Result]:', { data });

        customerStore.user = data?.customer ?? {};
      } catch {
        // eslint-disable-next-line no-void
        // @ts-ignore
        await logout();
      }
      error.value.load = null;
    } catch (err) {
      error.value.load = err;
      Logger.error('useUser/load', err);
    } finally {
      loading.value = false;
    }

    return customerStore.user;
  };

  // eslint-disable-next-line @typescript-eslint/require-await,no-empty-pattern
  const login = async ({ user: providedUser, customQuery, customHeaders }: UseUserLoginParams) : Promise<void> => {
    Logger.debug('[Magento] useUser.login', providedUser);
    resetErrorValue();

    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;

      const { data, errors } = await app.$vsf.$magento.api.generateCustomerToken(
        {
          email: providedUser.email,
          password: providedUser.password,
          recaptchaToken: providedUser.recaptchaToken,
        },
        customQuery || {},
        customHeaders || {},
      );
      Logger.debug('[Result]:', { data });

      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) => sendNotification({
          icon: 'error',
          id: Symbol(`registration_error-${i}`),
          message: app.i18n.t(registerError.message) as string,
          persist: false,
          title: 'Registration error',
          type: 'danger',
        }));
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }

      customerStore.setIsLoggedIn(true);
      apiState.setCustomerToken(data.generateCustomerToken.token);

      // merge existing cart with customer cart
      // todo: move this logic to separate method
      const currentCartId = apiState.getCartId();
      // vsf code
      // const cart = await app.context.$vsf.$magento.api.customerCart();
      const cart = await app.context.$vsf.$magento.api.customQuery({
        query: customerCart,
      }) as { data: CustomerCartQuery };

      const newCartId = cart.data.customerCart.id;

      try {
        if (newCartId && currentCartId && currentCartId !== newCartId) {
          // vsf code
          // const { data: dataMergeCart } = await app.context.$vsf.$magento.api.mergeCarts(
          //   {
          //     sourceCartId: currentCartId,
          //     destinationCartId: newCartId,
          //   },
          // );

          const { data: dataMergeCart } = await app.context.$vsf.$magento.api.customQuery({
            query: customMergeCartsGql,
            queryVariables: {
              sourceCartId: currentCartId,
              destinationCartId: newCartId,
            },
            customHeaders,
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(dataMergeCart.mergeCarts);

          apiState.setCartId(dataMergeCart.mergeCarts.id);
        } else {
          // @ts-ignore
          setCart(cart.data.customerCart);
        }
      } catch {
        // Workaround for Magento 2.4.4 mergeCarts mutation error related with Bundle products
        // It can be removed when Magento 2.4.5 will be release
        // @ts-ignore
        setCart(cart.data.customerCart);
      }
      error.value.login = null;
    } catch (err) {
      error.value.login = err;
      Logger.error('useUser/login', err);
    } finally {
      loading.value = false;
    }
  };

  const loadSocialLoginAccountData = async () => {
    Logger.debug('[Magento] useUser.loadSocialLoginAccountData');
    resetErrorValue();

    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({ query: amSocialLoginAccountData });

      customerStore.socialAccountData = data.amSocialLoginAccountData;

      Logger.debug('[Result]:', { data });

      error.value.loadAccountData = null;
    } catch (err) {
      error.value.loadAccountData = err;
      Logger.error('useUser/loadSocialLoginAccountData', err);
    } finally {
      loading.value = false;
    }
  };

  const loadSocialLoginButtonConfig = async () => {
    Logger.debug('[Magento] useUser.loadSocialLoginButtonConfig');
    resetErrorValue();

    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({ query: amSocialLoginButtonConfig });

      customerStore.socialButtonConfig = data.amSocialLoginButtonConfig;

      Logger.debug('[Result]:', { data });

      error.value.loadButtonConfig = null;
    } catch (err) {
      error.value.loadButtonConfig = err;
      Logger.error('useUser/loadSocialLoginButtonConfig', err);
    } finally {
      loading.value = false;
    }
  };

  const linkSocialAccount = async ({ type }: { type: String }): Promise<any> => {
    Logger.debug('[Magento] useUser.linkSocialAccount');
    resetErrorValue();
    let results = null;
    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: amSocialLoginLinkAccount,
        queryVariables: {
          type,
        },
      });
      results = data.amSocialLoginLinkAccount;

      Logger.debug('[Result]:', { data });

      error.value.linkSocialAccount = null;
    } catch (err) {
      error.value.linkSocialAccount = err;
      Logger.error('useUser/linkSocialAccount', err);
    } finally {
      loading.value = false;
    }
    return results;
  };

  const unlinkSocialAccount = async ({ type }: { type: String }): Promise<any> => {
    Logger.debug('[Magento] useUser.unlinkSocialAccount');
    resetErrorValue();
    let results = null;
    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: amSocialLoginUnlinkAccount,
        queryVariables: {
          type,
        },
      });
      results = data.amSocialLoginUnlinkAccount;

      Logger.debug('[Result]:', { data });

      error.value.unlinkSocialAccount = null;
    } catch (err) {
      error.value.unlinkSocialAccount = err;
      Logger.error('useUser/unlinkSocialAccount', err);
    } finally {
      loading.value = false;
    }
    return results;
  };

  const loginWithToken = async ({ token }: { token: string }) : Promise<void> => {
    Logger.debug('[Magento] useUser.loginWithToken', token);
    resetErrorValue();

    try {
      loading.value = true;
      const apiState = app.context.$vsf.$magento.config.state;
      if (!token) {
        return;
      }

      customerStore.setIsLoggedIn(true);
      apiState.setCustomerToken(token);

      const currentCartId = apiState.getCartId();

      const cart = await app.context.$vsf.$magento.api.customQuery({
        query: customerCart,
      }) as { data: CustomerCartQuery };

      const newCartId = cart.data.customerCart.id;

      try {
        if (newCartId && currentCartId && currentCartId !== newCartId) {
          const { data: dataMergeCart } = await app.context.$vsf.$magento.api.customQuery({
            query: customMergeCartsGql,
            queryVariables: {
              sourceCartId: currentCartId,
              destinationCartId: newCartId,
            },
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          setCart(dataMergeCart.mergeCarts);

          apiState.setCartId(dataMergeCart.mergeCarts.id);
        } else {
          // @ts-ignore
          setCart(cart.data.customerCart);
        }
      } catch {
        // Workaround for Magento 2.4.4 mergeCarts mutation error related with Bundle products
        // It can be removed when Magento 2.4.5 will be release
        // @ts-ignore
        setCart(cart.data.customerCart);
      }
      error.value.loginWithToken = null;
    } catch (err) {
      error.value.loginWithToken = err;
      Logger.error('useUser/login', err);
    } finally {
      loading.value = false;
    }
  };

  // eslint-disable-next-line consistent-return
  const register = async ({
    user: providedUser, isB2B,
  }: UseUserRegisterParams) : Promise<void> => {
    Logger.debug('[Magento] useUser.register', providedUser);
    resetErrorValue();

    try {
      loading.value = true;
      const {
        sales_channel, partnership_type, addresses, ...providedUserBase
      } = providedUser;
      const {
        email,
        password,
        recaptchaToken,
        ...baseData
      } = generateUserData({
        ...providedUserBase,
        ...(isB2B && {
          sales_channel,
          partnership_type,
          addresses,
        }),
      });

      const { data, errors } = await app.context.$vsf.$magento.api.customQuery({
        query: createCustomerV2Gql,
        queryVariables: {
          input: {
            email,
            password,
            ...baseData,
          },
        },
      });

      Logger.debug('[Result]:', { data });

      if (errors) {
        const joinedErrors = errors.map((e) => e.message).join(',');
        Logger.error(joinedErrors);
        errors.forEach((registerError, i) => sendNotification({
          icon: 'error',
          id: Symbol(`registration_error-${i}`),
          message: app.i18n.t(registerError.message as string) as string,
          persist: false,
          title: 'Registration error',
          type: 'danger',
        }));
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        throw new Error(joinedErrors);
      }
      error.value.register = null;

      if (isB2B) return null;

      let loginRecaptchaToken = '';
      if ($recaptcha && recaptchaToken) {
        loginRecaptchaToken = await $recaptcha.execute('login');
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { customer: { customer_create_account_confirm } } = app.context.$vsf.$magento.config;

      if (customer_create_account_confirm) {
        return await new Promise((resolve) => {
          sendNotification({
            id: Symbol('registration_confirmation'),
            message: app.i18n.t('Confirm your account and check email') as string,
            persist: false,
            title: 'Registration confirmation',
            type: 'success',
            icon: 'check',
          });

          resolve();
        });
      }

      await login({ user: { email, password, recaptchaToken: loginRecaptchaToken }, customQuery: {} });
    } catch (err) {
      error.value.register = err;
      Logger.error('useUser/register', err);
    } finally {
      loading.value = false;
    }
  };

  const getCustomAttributeMetadataV2 = async (attributeCodes: string[]): Promise<AttributesMetadataOutput> => {
    Logger.debug('[Magento] useUser.getCustomAttributeMetadataV2');
    let results = null;

    try {
      loading.value = true;
      const { data } = await app.context.$vsf.$magento.api.customQuery({
        query: customAttributeMetadataV2Gql,
        queryVariables: {
          attributes: attributeCodes.map((code) => ({ attribute_code: code, entity_type: 'customer' })),
        },
      });
      results = data.customAttributeMetadataV2;

      Logger.debug('[Result]:', { data });

      error.value.customAttributeMetadataV2 = null;
    } catch (err) {
      error.value.customAttributeMetadataV2 = err;
      Logger.error('useUser/getCustomAttributeMetadataV2', err);
    } finally {
      loading.value = false;
    }
    return results;
  };
  const isB2bUser = computed(() => customerStore.user?.is_b2b || false);

  return {
    setUser,
    updateUser,
    register,
    login,
    loginWithToken,
    logout,
    changePassword,
    load,
    loadSocialLoginAccountData,
    loadSocialLoginButtonConfig,
    linkSocialAccount,
    unlinkSocialAccount,
    loading: readonly(loading),
    error: readonly(error),
    user: computed(() => customerStore.user),
    socialAccountData: computed(() => customerStore.socialAccountData),
    socialButtonConfig: computed(() => customerStore.socialButtonConfig),
    isAuthenticated: computed(() => customerStore.isLoggedIn),
    saveCustomerNotify,
    getCustomAttributeMetadataV2,
    isB2bUser,
  };
}

export default useUser;
export * from './useUser';
